import StripePaymentLinkButton from "./StripePaymentLinkButton";
import React, { useState } from "react";
import { Copy, Check, DollarSign, Smartphone, HandHeart, X, Heart } from "lucide-react";
import { toast } from "sonner";

// EXACT same rails / labels / handles as the /donate page.
// No donation-system change — this file only re-uses the existing
// Cash App + Zelle block so it can be anchored to on the homepage.
const SUPPORT_RAILS = {
  cashapp: { handle: "$imnono7733", baseUrl: "https://cash.app/$imnono7733", label: "Cash App" },
  zelle:   { handle: "info@pawsonrecord.com", label: "Zelle" },
};
const CASHAPP_PRESETS = [5, 10, 25, 50, 100];

export default function CashAppZelleTip() {
  const [copied, setCopied] = useState(null);
  const [cashAmount, setCashAmount] = useState(25);
  const [cashCustom, setCashCustom] = useState("");
  const [zelleOpen, setZelleOpen] = useState(false);

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success(`Copied ${value}`);
    setTimeout(() => setCopied(null), 1800);
  };

  const activeCashAmount = () => {
    const custom = parseFloat(cashCustom);
    if (!isNaN(custom) && custom > 0) return Math.min(Math.round(custom), 5000);
    return cashAmount;
  };

  const openCashApp = () => {
    const amt = activeCashAmount();
    const url = `${SUPPORT_RAILS.cashapp.baseUrl}/${amt}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div id="donate-tip" className="border border-zinc-900 bg-zinc-900 text-white p-6" data-testid="home-donate-tip">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 border border-red-600 bg-red-600 flex items-center justify-center">
          <Heart size={18} />
        </div>
        <div className="label-eyebrow text-red-500">Support Paws on Record</div>
      </div>
      <div className="font-display font-black text-xl mt-4 leading-tight">Card · Apple Pay · Google Pay</div>
      <StripePaymentLinkButton testId="home-stripe-link" className="mt-3" dark />
      <div className="font-display font-black text-xl mt-6 pt-5 border-t border-zinc-700 leading-tight">
        Cash App / Zelle (no fees)
      </div>
      <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
        Prefer not to use a card? Tap to copy — paste into Cash App or your banking app.
      </p>

      <div className="mt-5 space-y-4">
        {/* CASH APP */}
        <div className="border border-zinc-700 bg-zinc-800/60 p-4" data-testid="home-cashapp-row">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-emerald-500 text-emerald-400 flex items-center justify-center text-sm font-black">$</div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{SUPPORT_RAILS.cashapp.label} — one tap</div>
              <div className="font-mono text-sm" data-testid="home-cashapp-handle">{SUPPORT_RAILS.cashapp.handle}</div>
            </div>
            <button
              onClick={() => handleCopy("cashapp", SUPPORT_RAILS.cashapp.handle)}
              className="p-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors"
              aria-label="Copy Cash App handle"
              data-testid="home-cashapp-copy-btn"
            >
              {copied === "cashapp" ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>

          <div className="mt-3 grid grid-cols-5 gap-1.5">
            {CASHAPP_PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => { setCashAmount(v); setCashCustom(""); }}
                data-testid={`home-cashapp-amount-${v}`}
                className={`py-2 text-xs font-black tracking-tight border transition-colors ${
                  cashAmount === v && !cashCustom
                    ? "bg-emerald-500 border-emerald-500 text-zinc-900"
                    : "border-zinc-700 text-zinc-200 hover:border-emerald-500"
                }`}
              >
                ${v}
              </button>
            ))}
          </div>

          <div className="mt-2 flex items-center gap-2 border border-zinc-700 bg-zinc-900/50 px-2">
            <DollarSign size={12} className="text-zinc-500" />
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="5000"
              placeholder="Custom amount"
              value={cashCustom}
              onChange={(e) => setCashCustom(e.target.value)}
              className="flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-zinc-500"
              data-testid="home-cashapp-custom-input"
            />
          </div>

          <button
            type="button"
            onClick={openCashApp}
            className="mt-3 w-full bg-emerald-500 text-zinc-900 hover:bg-emerald-400 transition-colors py-3 text-sm font-black uppercase tracking-widest inline-flex items-center justify-center gap-2"
            data-testid="home-cashapp-donate-btn"
          >
            <Smartphone size={16} /> Donate ${activeCashAmount()} with Cash App
          </button>
          <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
            Opens the Cash App mobile app when installed, or Cash App web checkout on desktop.
          </p>
        </div>

        {/* ZELLE */}
        <div className="border border-zinc-700 bg-zinc-800/60 p-4" data-testid="home-zelle-row">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-purple-400 text-purple-300 flex items-center justify-center text-sm font-black">Z</div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{SUPPORT_RAILS.zelle.label} — pay from your bank</div>
              <div className="font-mono text-sm" data-testid="home-zelle-handle">{SUPPORT_RAILS.zelle.handle}</div>
            </div>
            <button
              onClick={() => handleCopy("zelle", SUPPORT_RAILS.zelle.handle)}
              className="p-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors"
              aria-label="Copy Zelle address"
              data-testid="home-zelle-copy-btn"
            >
              {copied === "zelle" ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setZelleOpen(true)}
            className="mt-3 w-full bg-purple-500 text-white hover:bg-purple-400 transition-colors py-3 text-sm font-black uppercase tracking-widest inline-flex items-center justify-center gap-2"
            data-testid="home-zelle-donate-btn"
          >
            <HandHeart size={16} /> Donate with Zelle
          </button>
          <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
            Zelle payments must be sent from inside your bank's app — we'll show the recipient info + steps.
          </p>
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed mt-2">
          Tips to the registry aren't tax-deductible — if you need a receipt, donate to one of the 501(c)(3)s on the donate page instead.
        </p>
      </div>

      {/* ZELLE INSTRUCTIONS MODAL */}
      {zelleOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm px-4"
          onClick={() => setZelleOpen(false)}
          data-testid="home-zelle-modal-backdrop"
        >
          <div
            className="w-full max-w-md bg-white border border-zinc-900 shadow-2xl text-zinc-900"
            onClick={(e) => e.stopPropagation()}
            data-testid="home-zelle-modal"
          >
            <div className="flex items-center justify-between bg-zinc-900 text-white px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 border border-purple-400 text-purple-300 flex items-center justify-center text-xs font-black">Z</div>
                <div className="font-display font-black text-sm uppercase tracking-widest">Donate with Zelle</div>
              </div>
              <button onClick={() => setZelleOpen(false)} aria-label="Close" className="p-1 hover:text-red-400" data-testid="home-zelle-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="label-eyebrow">Send to</div>
              <div className="mt-2 flex items-center justify-between border border-zinc-900 bg-zinc-50 px-3 py-3">
                <div className="font-mono text-sm font-bold text-zinc-900 truncate" data-testid="home-zelle-modal-recipient">
                  {SUPPORT_RAILS.zelle.handle}
                </div>
                <button
                  onClick={() => handleCopy("zelle-modal", SUPPORT_RAILS.zelle.handle)}
                  className="flex-shrink-0 ml-3 px-3 py-2 border border-zinc-900 bg-white text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white transition-colors inline-flex items-center gap-1.5"
                  data-testid="home-zelle-modal-copy-btn"
                >
                  {copied === "zelle-modal" ? (<><Check size={12} /> Copied</>) : (<><Copy size={12} /> Copy</>)}
                </button>
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">
                Recipient name shown in Zelle: <span className="font-mono">Paws on Record</span>
              </div>

              <div className="mt-5 label-eyebrow">How to send</div>
              <ol className="mt-2 space-y-2 text-sm text-zinc-800 leading-relaxed">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">1</span>
                  <span>Open your bank's mobile app (Chase, BofA, Wells Fargo, etc.) and go to <b>Zelle</b> or <b>Send Money</b>.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">2</span>
                  <span>Paste the copied email <span className="font-mono">{SUPPORT_RAILS.zelle.handle}</span> as the recipient.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">3</span>
                  <span>Enter the amount you want to donate. Add "Paws on Record" in the memo (optional).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">4</span>
                  <span>Confirm and send — the payment goes bank-to-bank with no fees.</span>
                </li>
              </ol>

              <div className="mt-5 border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600 leading-relaxed">
                Zelle doesn't support a universal payment link — that's why donations must originate inside your own banking app. First-time Zelle senders may see a small verification step from their bank.
              </div>

              <button
                type="button"
                onClick={() => setZelleOpen(false)}
                className="mt-5 w-full py-3 bg-zinc-900 text-white text-sm font-black uppercase tracking-widest hover:bg-red-600 transition-colors"
                data-testid="home-zelle-modal-done"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
